import * as React from 'react';
import {useRef} from 'react';
import {
  createView,
  createViewConfig,
  view,
  assignProps,
  provider,
  useConstant,
  useIsomorphicLayoutEffect,
} from '@sail/engine';
import type {
  CollectionChildren,
  FocusableElement,
  Key,
  SingleSelection,
} from '@react-types/shared';
import type {AriaTabPanelProps, TabListState} from '@sail/react-aria';
import {useTabPanel, useTab, useTabList} from '@sail/react-aria';
import {chevronDown} from '@sail/icons/react/Icon';
import {tokens} from '@sail/tokens';
import {css} from '../css';
import {Menu, MenuItem} from './Menu';
import {Icon} from './Icon';
import type {ActionProps} from './Action';
import {Action} from './Action';
import {useTabListState} from '../collections-deprecated/react-aria-tabs';
import {useResizeObserver} from './hooks/useResizeObserver';
import type {Collection} from '../deprecated/components/collections';
import {
  CollectionProvider,
  useCollection,
  useItem,
} from '../deprecated/components/collections';

const {createContext, useContext, useState, useMemo, useEffect, useCallback} =
  React;

/**
 * @external
 */
interface TabProps extends ActionProps {
  /**
   * @external
   * Whether or not the tab should be disabled
   */
  disabled: boolean;
  /**
   * @external
   * Children passed to Tab component
   */
  children: React.ReactNode;

  ref?: React.Ref<HTMLAnchorElement>;
}

/**
 * TODO: This should expose the intrinsic props and forward them to
 * the div that this renders
 *
 * See https://git.corp.stripe.com/stripe-internal/pay-server/pull/587667
 *
 * @external
 */
interface TabsProps {
  /**
   * @external
   * Children passed to Tabs component
   */
  children: React.ReactNode;

  /**
   * @external
   * Size of Tabs component, can be 'small', 'medium' or 'large'
   */
  size: 'small' | 'medium' | 'large';

  /**
   * @external
   * Whether or not the tab list should take up the entire width of its container
   */
  fitted: boolean;

  /**
   * @external
   * Key of currently selected Tab panel (controlled)
   */
  selectedKey?: Key;

  /**
   * @external
   * The initially selected Tab panel (uncontrolled).
   */
  defaultSelectedKey?: Key;

  /**
   * @external
   * Callback to be fired when a Tab is selected
   */
  onSelectionChange?: (arg: Key) => void;

  /**
   * @external
   * className to be applied to the containing div
   */
  className?: string;

  dir: string;
}

export const TabsConfig = createViewConfig({
  name: 'Tabs',
  props: {} as TabsProps,
  flattens: true,
  defaults: {
    size: 'medium',
    fitted: false,
    dir: 'ltr',
  },
});

export const TabConfig = createViewConfig({
  name: 'Tab',
  props: {} as TabProps,
  flattens: true,
  defaults: {
    disabled: false,
  },
});

export const TabListConfig = createViewConfig({
  name: 'TabList',
  props: {} as {
    children: CollectionChildren<TabProps>;
  },
  flattens: true,
});

export const TabPanelConfig = createViewConfig({
  name: 'TabPanel',
  props: {} as AriaTabPanelProps & {children: React.ReactNode},
});

export const TabPanelsConfig = createViewConfig({
  name: 'TabPanels',
  props: {} as {children: React.ReactNode},
});

type ListState = TabListState<unknown> & {collapsed?: boolean};
type TabOptions = Pick<TabsProps, 'size' | 'dir' | 'fitted'> & SingleSelection;

const TabOptionsContext = createContext<TabOptions | null>(null);
const TabListStateContext = createContext<ListState | null>(null);
const TabListStateUpdaterContext = createContext<
  ((arg: ListState) => void) | null
>(null);

interface TabLineProps {
  dir?: 'ltr' | 'rtl';
}

/**
 * Underlines the currently selected tab, animating transitions between them.
 */
const TabLine = createView(
  ({dir, ...props}: TabLineProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const state = useContext(TabListStateContext);

    // intentionally using style instead of CSS to avoid inserting a style rule per pixel shift
    const [style, setStyle] = useState({width: '', transform: ''});

    const onResize = useCallback(() => {
      // find the selected tab from within the current container
      const selectedTab = ref.current?.parentElement?.querySelector(
        '[aria-selected="true"][data-key]:not([aria-hidden="true"])',
      ) as HTMLElement | undefined;

      if (selectedTab) {
        const updates = {transform: '', width: '', transition: ''};
        // eslint-disable-next-line @sail/ui-no-reflow
        const {offsetWidth, offsetLeft, offsetParent} = selectedTab;
        // eslint-disable-next-line @sail/ui-no-reflow
        const offsetParentWidth = (offsetParent as HTMLElement)?.offsetWidth;
        // In RTL, calculate the transform from the right edge of the tablist
        // so that resizing the window doesn't break the Tabline position due to
        // offsetLeft changes
        const offset =
          dir === 'rtl'
            ? -1 * (offsetParentWidth - offsetWidth - offsetLeft)
            : offsetLeft;

        updates.transform = `translateX(${offset}px)`;
        updates.width = `${offsetWidth}px`;

        if (
          style.width !== updates.width ||
          style.transform !== updates.transform
        ) {
          // don't animate the initial position of the Tabline
          if (style.transform) {
            updates.transition = 'transform 250ms';
          }

          setStyle(updates);
        }
      }
    }, [style, dir]);

    const node = useConstant(() => ({
      get current() {
        return ref.current?.parentElement || null;
      },
    }));

    useIsomorphicLayoutEffect(onResize, [
      onResize,
      state?.selectedKey,
      state?.collection,
    ]);
    useResizeObserver({node, handler: onResize, avoidInitialEvent: true});

    return <view.div role="presentation" {...props} style={style} ref={ref} />;
  },
  {
    css: {
      position: 'absolute',
      bottom: '-1px',
      height: 'xxsmall',
      // match the text color of the selected tab to the underline.
      backgroundColor: tokens['color.text.action.primary'],
    },
  },
);

/**
 * @external
 */
export const Tabs = TabsConfig.createView(
  ({children, className, inherits, ...props}) => {
    const [state, setState] = useState<ListState | null>(null);

    return (
      <view.div
        inherits={inherits}
        className={className}
        uses={[
          provider(TabOptionsContext, props),
          provider(TabListStateUpdaterContext, setState),
          provider(TabListStateContext, state),
        ]}
      >
        {children}
      </view.div>
    );
  },
  {
    variants: {
      fitted: {
        true: css({
          width: 'fill',
        }),
      },
    },
    forward: {
      fitted: true,
    },
  },
);

export const StyledTabConfig = createViewConfig({
  props: {} as {
    size?: TabsProps['size'];
    selected: boolean;
    children: React.ReactNode;
    hidden?: boolean;
  },
  flattens: true,
  context: false,
});

/**
 * Applies the styles of a tab, without exposing size/selected variants on the public Tabs API.
 * Component is used for the collapsed menu trigger and within the exported Tab component.
 */
const StyledTab = StyledTabConfig.createView((props) => <Action {...props} />, {
  css: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    font: 'label.medium.emphasized',
    color: 'subdued',
    transition: 'visibility 0s',
    stack: 'x',
    gapX: 'small',
    alignX: 'center',
  },
  uses: [
    assignProps(() => {
      // apply size applied from outer `Tabs` component
      const {size} = useContext(TabOptionsContext) || {};
      return {size: size || 'medium'};
    }),
  ],
  forward: {disabled: true},
  variants: {
    size: {
      small: css({
        paddingY: 'xsmall',
        font: 'label.small',
        fontWeight: 'semibold',
      }),
      medium: css({paddingY: 'medium'}),
      large: css({paddingY: 'space.250'}),
    },
    // TODO(koop): Figure out whether to add the prop or remove this line
    // @ts-expect-error disabled is not a prop on StyledTabContext
    disabled: {
      true: css({
        color: 'form.disabled',
      }),
      false: css({
        ':hover': {
          color: 'text',
        },
      }),
    },
    selected: {
      true: css({
        color: 'action.primary',
        ':hover': {
          color: 'action.primary.hovered',
        },
      }),
    },
    hidden: {
      true: css({visibility: 'hidden'}),
    },
  },
});

/**
 * @external
 */
export const Tab = TabConfig.createView((props) => {
  const {id, children, disabled} = props;
  const ref = useRef<HTMLAnchorElement>(null);
  const data = useMemo(() => ({children, disabled}), [children, disabled]);
  const {key} = useItem({typeaheadValue: '', id, data});
  const state = useContext(TabListStateContext) as ListState;
  const {
    tabProps: {
      onPointerDown: tabOnPointerDown,
      onClick: tabOnClick,
      ...restTabProps
    },
    isSelected,
  } = useTab({key, isDisabled: disabled}, state, ref);

  // for both pointerDown and click, we want to prevent the tab from changing
  // if the tab is also a link and the user is holding a modifier key
  const onPointerDown: React.PointerEventHandler<FocusableElement> = (e) => {
    if (props.href && (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey)) {
      return;
    }
    tabOnPointerDown?.(e);
  };
  const onClick: React.MouseEventHandler<FocusableElement> = (e) => {
    if (props.href && (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey)) {
      return;
    }
    tabOnClick?.(e);
  };
  // TODO(craigcav): This pattern is likely not compatible with concurrent-mode.
  ///   Can we modify this data in an effect?
  //
  // sync the disabled prop back into state
  if (disabled) {
    state?.disabledKeys.add(key);
  } else {
    state?.disabledKeys.delete(key);
  }
  const variants = {selected: isSelected, hidden: state.collapsed};
  return (
    <StyledTab
      {...props}
      // we manually modify and pass onPointerDown, which isn't on the interface
      // but which the underlying Action can accept
      // @ts-expect-error onPointerDown does not exist on type...
      onPointerDown={onPointerDown}
      onClick={onClick}
      {...restTabProps}
      {...variants}
      ref={ref}
    />
  );
});

const TabPicker = createView((props) => {
  const state = useContext(TabListStateContext);
  const selectedItem = state?.selectedItem;

  if (!selectedItem) {
    return <React.Fragment />;
  }

  return (
    <React.Fragment>
      <Menu
        {...props}
        trigger={
          <StyledTab
            css={{position: 'absolute'}}
            aria-hidden={state.collapsed ? undefined : true}
            hidden={!state.collapsed}
            selected
          >
            {selectedItem.props.children}
            <Icon icon={chevronDown} size="xsmall" />
          </StyledTab>
        }
        onAction={(key: Key) => state.setSelectedKey(key)}
      >
        {[...state.collection].map(({key, props}) => (
          <MenuItem id={key as string} key={key} disabled={props.disabled}>
            {props.children}
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
});

/**
 * @external
 */
export const TabList = TabListConfig.createView(
  ({children, ...props}) => {
    const ref = useRef<HTMLDivElement>(null);
    const collection = useCollection<TabProps>(children as React.ReactNode);
    const {selectedKey, defaultSelectedKey, onSelectionChange, dir, fitted} =
      useContext(TabOptionsContext) || {};

    const state = useTabListState({
      getItems: collection.getItems,
      selection: {selectedKey, defaultSelectedKey, onSelectionChange},
    });

    const {tabListProps} = useTabList({}, state, ref);

    const setTabState = useContext(TabListStateUpdaterContext);

    useEffect(() => {
      // Passing back to root as useTabPanel needs the TabListState
      setTabState?.(state);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.disabledKeys, state.selectedItem, state.selectedKey]);

    const [collapsed, setCollapsed] = useState(false);

    const checkShouldCollapse = useCallback(() => {
      const el = ref.current;
      if (ref.current) {
        const tabs = el?.querySelectorAll('[role="tab"]') || [];
        const tabDimensions = [...tabs].map((tab) =>
          // eslint-disable-next-line @sail/ui-no-reflow
          tab.getBoundingClientRect(),
        );
        const end = dir === 'rtl' ? 'left' : 'right';
        // eslint-disable-next-line @sail/ui-no-reflow
        const farEdgeTabList = Math.ceil(el?.getBoundingClientRect()[end] || 0);
        const farEdgeLastTab = Math.floor(
          tabDimensions[tabDimensions.length - 1][end] || 0,
        );
        const shouldCollapse =
          dir === 'rtl'
            ? farEdgeLastTab < farEdgeTabList
            : farEdgeTabList < farEdgeLastTab;
        setCollapsed(shouldCollapse);
      }
    }, [ref, dir, setCollapsed]);

    useEffect(checkShouldCollapse, [children, checkShouldCollapse]);
    useResizeObserver({node: ref, handler: checkShouldCollapse});

    return (
      <view.div
        {...props}
        {...tabListProps}
        css={fitted ? css({alignX: 'stretch'}) : css({alignX: 'start'})}
        ref={ref}
      >
        <TabListStateContext.Provider value={Object.assign(state, {collapsed})}>
          <TabPicker />
          <CollectionProvider collection={collection as Collection<unknown>}>
            {collection.children}
          </CollectionProvider>
        </TabListStateContext.Provider>
        {!collapsed ? <TabLine /> : null}
      </view.div>
    );
  },
  {
    css: {
      position: 'relative',
      stack: 'x',
      gap: 'large',
      borderColor: 'border',
      borderBottomWidth: 'small',
      borderBottomStyle: 'solid',
    },
  },
);

/**
 * @external
 */
export const TabPanel = TabPanelConfig.createView(
  ({children, ...props}) => {
    const state = useContext(TabListStateContext) as ListState;
    const ref = React.useRef<HTMLDivElement>(null);
    const {tabPanelProps} = useTabPanel(props, state, ref);

    const selected = props.id && state?.selectedKey === props.id;

    return selected ? (
      <view.div ref={ref} {...props} {...tabPanelProps}>
        {children}
      </view.div>
    ) : null;
  },
  {
    css: {
      ':focus-visible': {
        focusRing: 'focus',
      },
    },
  },
);

/**
 * @external
 */
export const TabPanels = TabPanelsConfig.createView(({children}) => {
  const state = useContext(TabListStateContext);

  return (
    <React.Fragment key={state?.selectedKey}>
      {state && state.selectedKey ? children : null}
    </React.Fragment>
  );
});
